import React from 'react';
import * as Sentry from '@sentry/react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import {
  HashRouter,
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from 'react-router';

import { version } from '../package.json';

import { store } from './redux/configureStore';
import App from './App.tsx';
import './index.css';
import './localization/i18n';
import { applyDomSafetyPatch } from './utils/domSafetyPatch';

applyDomSafetyPatch();

// DSNs are safe to keep public https://docs.sentry.io/product/sentry-basics/concepts/dsn-explainer/
Sentry.init({
  // @ts-expect-error window cypress
  dsn: window.Cypress
    ? ''
    : 'https://1f9621c32cf2fdf1a774a87fb00e5a46@o16484.ingest.us.sentry.io/4508172536446976',
  release: `react-vite@${version}`,
  denyUrls: [
    /localhost/,
    /extensions\//i,
    /^chrome:\/\//i,
    /safariextension:/i,
    /moz-extension:/i,
  ],
  integrations: [
    Sentry.replayIntegration(),
    Sentry.reactRouterV7BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
  ],
  beforeSend(event) {
    const benignErrors = [
      'Failed to fetch dynamically imported module',
      'Importing a module script failed',
      // 'Network request failed',
      'Load failed', // iOS Safari connectivity drop
      'NetworkError', // Firefox offline
      'T.currentTarget.contains',
    ];

    const errorMessage =
      event.message || event.exception?.values?.[0]?.value || '';

    if (benignErrors.some((msg) => errorMessage.includes(msg))) {
      return null;
    }

    return event;
  },
  // Tracing
  tracesSampleRate: 0.25, //  Capture 25% of the transactions
  tracePropagationTargets: [/^https:\/\/seamless\.md\/api/],
  // Session Replay
  replaysOnErrorSampleRate: 1.0,
});

// When a new deployment changes chunk hashes, browsers with a cached index.html
// will try to load stale chunk URLs that no longer exist → blank page.
// This forces a reload so the browser picks up the fresh index.html and new hashes.
window.addEventListener('vite:preloadError', () => {
  window.location.reload();
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Provider store={store}>
      <HashRouter>
        <App />
      </HashRouter>
    </Provider>
  </StrictMode>
);
